import { useEffect, useMemo, useState } from 'react';
import { BasketContext } from './basket-context';
import omit from 'lodash/omit';
import useDebounce from '@wearetla/tla-essentials-tools/hooks/debounce';

// Services
import orderServices from '/services/order';
import basketServices from '/services/basket';

// Context
import { useBranchSelection } from '/utilities/branch-selection';
import { useNavigator } from '@wearetla/tla-essentials-tools/utilities/navigator';
// import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals'

// Functions
import formatNumber from '@wearetla/tla-essentials-tools/functions/format-number';
import isExact from '@wearetla/tla-essentials-tools/functions/is-exact';
import { useAuth } from '@wearetla/tla-essentials-tools/utilities/auth';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';

const basketPersistenceKey = 'basket-content';

const parsePrice = (price) => ({
	currency_prefix: null,
	currency_suffix: 'TL',
	price: price,
	price_formatted: formatNumber(price),
	old_price: null,
	old_price_formatted: null,
});

export const BasketProvider = ({ children }) => {
	const [initialized, setInitialized] = useState(false);
	const [indexedItems, setIndexedItems] = useState({});
	const [basketBranch, setBasketBranch] = useState(null);
	const [busy, setBusy] = useState(true);
	const [items, setItems] = useState(null);

	const { loggedIn } = useAuth();
	const { openModal } = useModals();
	const {
		selectedBranch,
		tableNumber,
		initialized: branchSelectionInitialized,
	} = useBranchSelection();
	const { redirect } = useNavigator();

	const debouncedIndexedItems = useDebounce(indexedItems, 500);

	// Değişen sepet için debounce istek at.
	useEffect(() => {
		if (
			selectedBranch?.id &&
			basketBranch?.id &&
			selectedBranch.id === basketBranch.id &&
			isExact(debouncedIndexedItems, indexedItems) &&
			Object.keys(debouncedIndexedItems).length
		) {
			setBusy(true);
			const itemsPayload = Object.keys(debouncedIndexedItems).map(
				(productId) => debouncedIndexedItems[productId]
			);
			basketServices
				.get(itemsPayload, basketBranch.id)
				.then((items) => {
					const { filteredItems, removedIds } = items.reduce(
						({ filteredItems, removedIds }, item) => {
							return {
								filteredItems: item.status === 1 ? [...filteredItems, item] : filteredItems,
								removedIds: item.status === 1 ? removedIds : [...removedIds, item.id],
							};
						},
						{ filteredItems: [], removedIds: [] }
					);

					setItems(filteredItems);

					if (removedIds.length) {
						setIndexedItems((curIndexedItems) => omit(curIndexedItems, removedIds));
					}
					setInitialized(true);
				})
				.catch((error) => {
					console.error('Sepet hatası', error);
					setIndexedItems({});
					setInitialized(true);
				})
				.finally(() => {
					setBusy(false);
				});
		}
	}, [debouncedIndexedItems, indexedItems, basketBranch, selectedBranch]);

	// Boş sepeti anında temizle
	useEffect(() => {
		if (initialized && !(basketBranch?.id && Object.keys(indexedItems).length)) {
			setItems([]);
			setBusy(false);
		}
	}, [indexedItems, initialized]);

	const [total, itemCount] = useMemo(() => {
		const [total, itemCount] = items?.reduce?.(
			([total, itemCount], item) => {
				return [total + parseFloat(item.total_price), itemCount + item.quantity];
			},
			[0, 0]
		) ?? [0, 0];

		return [parsePrice(total), itemCount];
	}, [items]);

	const clearBasket = () => {
		setIndexedItems({});
	};

	const setProductQuantity = (product, quantity) => {
		if (product?.id && product?.status === 1) {
			setIndexedItems((indexedItems) => {
				const itemsWithoutProduct = omit(indexedItems, product.id);
				return {
					...itemsWithoutProduct,
					...(quantity
						? {
								[product.id]: {
									quantity,
									merchant_id: product.merchant_id,
									menu_id: product.merchant_menu_id,
									id: product.id,
								},
							}
						: {}),
				};
			});
		} else if (product?.status !== 1) {
			openModal('message', {
				message: 'Bu ürünü şu an sepetine ekleyemezsin.',
			});
		}
	};

	const prepareSale = (productNotes) => {
		if (itemCount) {
			if (!loggedIn) {
				openModal('confirm', {
					title: 'Giriş Yapmalısın',
					message: 'Ödeme adımına geçmek için giriş yapman gerekiyor.',
					confirmText: 'Giriş yap',
					onConfirm: () => {
						openModal('auth');
					},
				});
			} else {
				setBusy(true);
				orderServices
					.prepare(
						items.reduce((items, item) => {
							return [
								...items,
								...[...Array(item.quantity)].map(() => ({
									id: item.id,
									menu_id: item.merchant_menu_id,
									merchant_id: item.merchant_id,
									note: productNotes[item.id],
								})),
							];
						}, []),
						selectedBranch.slug,
						tableNumber
					)
					.then((sale) => {
						setBusy(false);
						redirect('payment', { orderId: sale.id });
					})
					.catch(() => {
						setBusy(false);
					});
			}
		}
	};

	useEffect(() => {
		if (!initialized && branchSelectionInitialized) {
			try {
				const parsedStorageData = JSON.parse(localStorage.getItem(basketPersistenceKey));

				if (
					typeof parsedStorageData === 'object' &&
					parsedStorageData?.branchId &&
					parsedStorageData.branchId === selectedBranch?.id &&
					Object.keys(parsedStorageData?.items ?? {}).length
				) {
					setBasketBranch(selectedBranch);
					setIndexedItems(parsedStorageData.items);

					if (Object.keys(parsedStorageData.items).length === 0) {
						setInitialized(true);
					}
				} else {
					localStorage.removeItem(basketPersistenceKey);
					setBasketBranch(null);
					setIndexedItems({});
					setItems([]);
					setBusy(false);
					setInitialized(true);
				}
			} catch (e) {
				localStorage.removeItem(basketPersistenceKey);
				setBasketBranch(null);
				setIndexedItems({});
				setItems([]);
				setBusy(false);
				setInitialized(true);
			}
		}
	}, [selectedBranch, initialized, branchSelectionInitialized]);

	useEffect(() => {
		if (initialized && basketBranch?.id !== selectedBranch?.id) {
			clearBasket();
			setBasketBranch(selectedBranch);
		}
	}, [selectedBranch, basketBranch, initialized]);

	useEffect(() => {
		if (initialized && selectedBranch?.id) {
			localStorage.setItem(
				basketPersistenceKey,
				JSON.stringify({
					items: indexedItems,
					branchId: selectedBranch.id,
				})
			);
		}
	}, [selectedBranch, initialized, indexedItems]);

	return (
		<BasketContext.Provider
			value={{
				initialized,
				indexedItems,
				items: items ?? [],
				itemCount,
				total,
				busy,
				prepareSale,
				setProductQuantity,
				clearBasket,
			}}
		>
			{children}
		</BasketContext.Provider>
	);
};
